import { StatTile, rateTone } from "./Evidence";
import { inr, num, pct } from "../lib/format";

const Section = ({ title, note, children, testId }) => (
  <section className="rounded-xl border border-stone-200 bg-white p-4 space-y-3 break-inside-avoid" data-testid={testId}>
    <div className="flex items-baseline justify-between gap-3 flex-wrap">
      <h3 className="text-[13px] font-semibold text-stone-800">{title}</h3>
      {note && <span className="text-[12px] text-stone-600">{note}</span>}
    </div>
    {children}
  </section>
);

const Row = ({ label, value, sub }) => (
  <li className="flex items-baseline justify-between gap-3 py-1 border-b border-stone-100 last:border-0">
    <span className="text-[13px] text-stone-800 min-w-0 truncate">{label}</span>
    <span className="num text-[13px] font-semibold text-stone-900 shrink-0">
      {value}{sub && <span className="font-normal text-stone-600"> · {sub}</span>}
    </span>
  </li>
);

/** The whole portfolio on one page: spend, delivery, partners, peer position, aspirational reach. */
export const BoardNote = ({ note }) => {
  const p = note.portfolio, d = note.delivery, pt = note.partners, pp = note.peer_position, adp = note.aspirational;
  const fy = p.fy_range || [];
  return (
    <div className="space-y-4" data-testid="board-note">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
        <StatTile label="Committed" tone="brand" value={inr(p.committed_inr)}
                  sub={`${num(p.grants)} grants · ${fy[0]}–${fy[fy.length - 1]}`} testId="board-committed" />
        <StatTile label="Utilisation" tone={rateTone(d.utilisation_rate_pct)} value={pct(d.utilisation_rate_pct)}
                  sub={`${num(d.closed_grants)} ended grants · ${num(d.in_flight_grants)} in flight`} testId="board-utilisation" />
        <StatTile label="Recoverable" tone={d.recoverable_inr >= 1e6 ? "rose" : "emerald"} value={inr(d.recoverable_inr)}
                  sub={`${d.recoverable_districts.length} districts above the floor`} testId="board-recoverable" />
        <StatTile label="Reach" tone="sky" value={`${num(p.states)} states`}
                  sub={`${num(p.districts)} districts · ${num(p.partners)} partners`} testId="board-reach" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Committed by financial year" note={p.open_fys?.length ? `${p.open_fys.join(", ")} still open` : null}
                 testId="board-by-fy">
          <ul>
            {Object.entries(p.committed_by_fy_inr || {}).map(([k, v]) => (
              <Row key={k} label={k} value={inr(v)} sub={p.open_fys?.includes(k) ? "open" : null} />
            ))}
          </ul>
        </Section>
        <Section title="Committed by theme" note="all years together" testId="board-by-theme">
          <ul>
            {Object.entries(p.committed_by_theme_inr || {}).map(([k, v]) => (
              <Row key={k} label={k} value={inr(v)} sub={pct(100 * v / p.committed_inr)} />
            ))}
          </ul>
        </Section>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Delivery — money to recover" note={`${d.flagged_districts} of ${d.districts_total} districts flagged`}
                 testId="board-delivery">
          {d.recoverable_districts.length === 0 ? (
            <p className="text-[13px] text-stone-700">No district holds material unutilised money on ended grants.</p>
          ) : (
            <ul>
              {d.recoverable_districts.map((c) => (
                <Row key={c.cell} label={c.cell} value={inr(c.stalled_inr)} sub={`${pct(c.utilisation_rate_pct)} utilised`} />
              ))}
            </ul>
          )}
          {d.verify_reporting_districts?.length > 0 && (
            <p className="text-[12px] text-stone-600 leading-relaxed">
              Verify reporting (beneficiary counts look under-reported): {d.verify_reporting_districts.slice(0, 5).join(", ")}
              {d.verify_reporting_districts.length > 5 ? ` +${d.verify_reporting_districts.length - 5} more` : ""}.
            </p>
          )}
          <p className="text-[12px] text-stone-600 leading-relaxed">
            Top-5 districts hold {pct(d.top5_share_pct)} of committed value.
          </p>
        </Section>

        <Section title="Partners — who needs a review" note={`${num(pt.total)} partners`} testId="board-partners">
          <div className="grid grid-cols-3 gap-2">
            <StatTile label="DD approved" tone="emerald" value={num(pt.dd_status_counts?.approved)}
                      sub={`${num(pt.dd_status_counts?.pending)} pending`} />
            <StatTile label="No DD record" tone={pt.no_dd_count ? "amber" : "emerald"} value={num(pt.no_dd_count)}
                      sub={inr(pt.no_dd_committed_inr)} />
            <StatTile label="Below 85%" tone={pt.below_85_count ? "rose" : "emerald"} value={num(pt.below_85_count)}
                      sub={`${inr(pt.below_85_unspent_inr)} unutilised`} />
          </div>
          {pt.weakest?.length > 0 && (
            <ul>
              {pt.weakest.map((w) => (
                <Row key={w.partner} label={w.partner} value={pct(w.utilisation_rate_pct)}
                     sub={`${inr(w.unspent_inr)} on ${w.closed_grants} ended grant${w.closed_grants === 1 ? "" : "s"}`} />
              ))}
            </ul>
          )}
        </Section>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Peer position — MCA basis" note={`${pp.first_fy} to ${pp.fy}, all themes`} testId="board-peers">
          <div className="grid grid-cols-2 gap-2">
            <StatTile label={`Filed ${pp.fy}`} tone="brand" value={inr(pp.bajaj_filed_inr)}
                      sub={`from ${inr(pp.bajaj_filed_first_inr)} in ${pp.first_fy}`} />
            <StatTile label="Share of BFSI peers" tone="sky" value={pct(pp.share_of_bfsi_pct)}
                      sub={`${pct(pp.share_of_bfsi_pct_lower)} at wider tagging · from ${pct(pp.share_of_bfsi_first_pct)}`} />
          </div>
          <p className="text-[12px] text-stone-600 leading-relaxed">
            {pct(pp.share_of_all_pct)} of all {num(pp.peer_company_count)} filers; BFSI peer set {num(pp.bfsi_company_count)}–{num(pp.bfsi_company_count_upper)} companies.
            Filed spend is a different measure from mGrant committed value and is never added to it.
          </p>
        </Section>

        {adp && (
          <Section title="Aspirational districts — NITI Aayog" testId="board-adp">
            <div className="grid grid-cols-2 gap-2">
              <StatTile label="Districts funded" tone="sky"
                        value={`${num(adp.aspirational_districts_funded)} of ${num(adp.aspirational_districts_in_list)}`} />
              <StatTile label="Committed there" tone="brand" value={inr(adp.committed_in_aspirational_districts)}
                        sub={`${pct(adp.committed_share_in_aspirational_pct)} of the portfolio`} />
            </div>
          </Section>
        )}
      </div>

      <section className="rounded-xl border border-stone-200 bg-stone-50/70 p-4" data-testid="board-caveats">
        <h3 className="text-[13px] font-semibold text-stone-800 mb-1.5">What these figures do and do not say</h3>
        <ul className="space-y-1">
          {(note.notes || []).map((n, i) => (
            <li key={i} className="text-[12px] text-stone-700 leading-relaxed flex gap-2">
              <span className="w-1 h-1 rounded-full bg-[#A01236] mt-1.5 shrink-0" />
              <span>{n}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};
